/**
 * Configuration — Keywords
 * Paired configuration for the locked Keyword source register.
 * Controls where eligible Keywords may be used across fixed Application Areas.
 * Does not create, rename, activate, inactivate or delete Keyword source records.
 */

import { buildSampleTradeCategories } from '../sections/AdminDashboard/TradeCategories/tradeCategoriesDummyData';
import { getTradeSubcategories } from './tradeCategoryWorkflow';
import {
  formatConfigAuditDisplay,
  formatConfigLastUpdatedDisplay,
  formatConfigListDate,
  formatSourceLabel,
} from './tradeCategoryConfig';

export {
  formatConfigAuditDisplay,
  formatConfigLastUpdatedDisplay,
  formatConfigListDate,
  formatSourceLabel,
};

export const ALL_SCOPE_SELECTION_LABEL = 'All Keywords';

export const KEYWORD_CONFIG_ROUTE = '/admin/configuration/keywords';

export const APPLICATION_AREA = {
  POST_CREATION: 'Post Creation',
  BUSINESS_PROFILE: 'Business Profile',
  SEARCH_MATCHING_HANDOFF: 'Search & Matching Handoff',
};

export const APPLICATION_AREA_OPTIONS = [
  { value: APPLICATION_AREA.POST_CREATION, label: APPLICATION_AREA.POST_CREATION },
  { value: APPLICATION_AREA.BUSINESS_PROFILE, label: APPLICATION_AREA.BUSINESS_PROFILE },
  {
    value: APPLICATION_AREA.SEARCH_MATCHING_HANDOFF,
    label: APPLICATION_AREA.SEARCH_MATCHING_HANDOFF,
  },
];

export const APPLICATION_AREA_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...APPLICATION_AREA_OPTIONS,
];

export const RULE_SCOPE = {
  ALL_KEYWORDS: 'All Keywords',
  SELECTED_TRADE_CATEGORIES: 'Selected Trade Categories',
  SELECTED_SUBCATEGORIES: 'Selected Subcategories',
};

export const RULE_SCOPE_OPTIONS = [
  { value: RULE_SCOPE.ALL_KEYWORDS, label: RULE_SCOPE.ALL_KEYWORDS },
  { value: RULE_SCOPE.SELECTED_TRADE_CATEGORIES, label: RULE_SCOPE.SELECTED_TRADE_CATEGORIES },
  { value: RULE_SCOPE.SELECTED_SUBCATEGORIES, label: RULE_SCOPE.SELECTED_SUBCATEGORIES },
];

export const KEYWORD_USE = {
  ENABLED: 'Enabled',
  DISABLED: 'Disabled',
};

export const KEYWORD_USE_OPTIONS = [
  { value: KEYWORD_USE.ENABLED, label: KEYWORD_USE.ENABLED },
  { value: KEYWORD_USE.DISABLED, label: KEYWORD_USE.DISABLED },
];

export const CONFIGURATION_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const CONFIGURATION_STATUS_OPTIONS = [
  { value: CONFIGURATION_STATUS.ACTIVE, label: CONFIGURATION_STATUS.ACTIVE },
  { value: CONFIGURATION_STATUS.INACTIVE, label: CONFIGURATION_STATUS.INACTIVE },
];

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  ...CONFIGURATION_STATUS_OPTIONS,
];

export const MESSAGES = {
  empty: 'No Keyword configuration rules have been added.',
  noResults: 'No Keyword configuration rules match the selected search or filters.',
  saved: 'Keyword configuration rule saved successfully.',
  updated: 'Keyword configuration rule updated successfully.',
  activated: 'Keyword configuration rule activated successfully.',
  inactivated: 'Keyword configuration rule made Inactive successfully.',
  overlap:
    'This rule overlaps another Active rule in the same Application Area. Change the Rule Scope or Scope Selection before saving.',
  failedApply:
    'Keyword configuration rule could not be applied. The current configuration remains in use.',
};

/** Trade Category source options for Configuration — Keywords scope selection. */
export function getKeywordConfigSources() {
  return buildSampleTradeCategories();
}

export function getSourceTradeCategoryOptions(sources = getKeywordConfigSources()) {
  return (sources || [])
    .map((c) => ({ id: c.id, name: c.name, label: formatSourceLabel(c.name, c.id) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getSourceSubcategoryOptions(
  parentIds = [],
  sources = getKeywordConfigSources(),
) {
  const parents = new Set(parentIds || []);
  const options = [];
  (sources || []).forEach((category) => {
    if (parents.size && !parents.has(category.id)) return;
    getTradeSubcategories(category).forEach((sub) => {
      options.push({
        id: sub.id,
        name: sub.name,
        parentId: category.id,
        parentName: category.name,
        label: formatSourceLabel(sub.name, sub.id),
        parentLabel: formatSourceLabel(category.name, category.id),
        scopeLabel: `${category.name}: ${formatSourceLabel(sub.name, sub.id)}`,
      });
    });
  });
  return options.sort((a, b) => a.name.localeCompare(b.name));
}

export function nextKeywordRuleId(existing = []) {
  const nums = (existing || [])
    .map((r) => {
      const match = String(r.id || '').match(/^KWR-(\d+)$/i);
      return match ? Number(match[1]) : 0;
    })
    .filter((n) => n > 0);
  const next = nums.length ? Math.max(...nums) + 1 : 1;
  return `KWR-${String(next).padStart(4, '0')}`;
}

export function emptyKeywordRuleForm() {
  return {
    id: '',
    ruleName: '',
    applicationArea: '',
    ruleScope: '',
    tradeCategoryIds: [],
    parentTradeCategoryIds: [],
    subcategoryIds: [],
    keywordUse: '',
    configurationStatus: CONFIGURATION_STATUS.ACTIVE,
    createdBy: '',
    createdOn: '',
    lastUpdatedBy: '',
    lastUpdatedOn: '',
    changeHistory: [],
  };
}

function scopeNameFragment(form = {}, sources = getKeywordConfigSources()) {
  if (form.ruleScope === RULE_SCOPE.ALL_KEYWORDS) return '';

  if (form.ruleScope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    const names = (form.tradeCategoryIds || [])
      .map((id) => (sources || []).find((c) => c.id === id)?.name)
      .filter(Boolean);
    return names.join(', ');
  }

  if (form.ruleScope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const names = getSourceSubcategoryOptions(form.parentTradeCategoryIds || [], sources)
      .filter((o) => (form.subcategoryIds || []).includes(o.id))
      .map((o) => o.name);
    return names.join(', ');
  }

  return '';
}

export function buildRuleBehaviourSummary(form = {}, sources = getKeywordConfigSources()) {
  const area = form.applicationArea || '';
  const use = form.keywordUse || '';
  const fragment = scopeNameFragment(form, sources);
  const isAll = form.ruleScope === RULE_SCOPE.ALL_KEYWORDS;
  const enabled = use === KEYWORD_USE.ENABLED;

  if (area === APPLICATION_AREA.POST_CREATION) {
    if (enabled) {
      if (isAll || !fragment) {
        return 'Eligible Keywords available during Post creation and editing';
      }
      return `Eligible ${fragment} Keywords available during Post creation and editing`;
    }
    if (isAll || !fragment) {
      return 'Keywords unavailable during Post creation and editing';
    }
    return `${fragment} Keywords unavailable during Post creation and editing`;
  }

  if (area === APPLICATION_AREA.BUSINESS_PROFILE) {
    if (enabled) {
      if (isAll || !fragment) {
        return 'Eligible stored Keyword Names displayed on Business Profiles';
      }
      return `Eligible stored ${fragment} Keyword Names displayed on public Business Profiles`;
    }
    if (isAll || !fragment) {
      return 'Stored Keyword Names hidden from public Business Profiles';
    }
    return `Stored ${fragment} Keyword Names hidden from public Business Profiles`;
  }

  if (area === APPLICATION_AREA.SEARCH_MATCHING_HANDOFF) {
    if (enabled) {
      if (isAll || !fragment) {
        return 'Eligible Keyword relationships supplied to Search & Matching Handoff';
      }
      return `Eligible ${fragment} Keyword relationships supplied to Search & Matching Handoff`;
    }
    if (isAll || !fragment) {
      return 'Keyword relationships excluded from Search & Matching Handoff';
    }
    return `${fragment} Keyword relationships excluded from Search & Matching Handoff`;
  }

  return 'Generated from the confirmed field selections';
}

export function formatScopeSelectionDisplay(rule, sources = getKeywordConfigSources()) {
  const scope = rule.ruleScope;
  if (scope === RULE_SCOPE.ALL_KEYWORDS) {
    return ALL_SCOPE_SELECTION_LABEL;
  }

  if (scope === RULE_SCOPE.SELECTED_TRADE_CATEGORIES) {
    const ids = rule.tradeCategoryIds || [];
    if (!ids.length) return '—';
    return ids
      .map((id) => {
        const cat = (sources || []).find((c) => c.id === id);
        return cat ? formatSourceLabel(cat.name, cat.id) : id;
      })
      .join('; ');
  }

  if (scope === RULE_SCOPE.SELECTED_SUBCATEGORIES) {
    const subIds = new Set(rule.subcategoryIds || []);
    if (!subIds.size) return '—';
    return getSourceSubcategoryOptions(rule.parentTradeCategoryIds || [], sources)
      .filter((o) => subIds.has(o.id))
      .map((o) => o.scopeLabel)
      .join('; ');
  }

  return '—';
}

export function normalizeKeywordRule(raw = {}, sources = getKeywordConfigSources()) {
  const base = {
    ...emptyKeywordRuleForm(),
    ...raw,
    tradeCategoryIds: [...(raw.tradeCategoryIds || [])],
    parentTradeCategoryIds: [...(raw.parentTradeCategoryIds || [])],
    subcategoryIds: [...(raw.subcategoryIds || [])],
    changeHistory: [...(raw.changeHistory || [])],
  };
  return {
    ...base,
    ruleBehaviour: buildRuleBehaviourSummary(base, sources),
    scopeSelectionDisplay: formatScopeSelectionDisplay(base, sources),
  };
}

export function createDefaultKeywordRuleForm() {
  return emptyKeywordRuleForm();
}

export function findKeywordRule(rules, ruleId) {
  return (rules || []).find((r) => r.id === ruleId) ?? null;
}

export function findDuplicateRuleName(rules, ruleName, excludeId) {
  const name = String(ruleName || '')
    .trim()
    .toLowerCase();
  if (!name) return null;
  return (
    (rules || []).find(
      (r) =>
        r.id !== excludeId &&
        String(r.ruleName || '')
          .trim()
          .toLowerCase() === name,
    ) || null
  );
}

export function matchesKeywordRuleSearch(rule, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase();
  if (!q) return true;
  return (
    String(rule.id || '')
      .toLowerCase()
      .includes(q) ||
    String(rule.ruleName || '')
      .toLowerCase()
      .includes(q)
  );
}

export function matchesKeywordRuleFilters(rule, { applicationAreaFilter, statusFilter } = {}) {
  if (applicationAreaFilter && applicationAreaFilter !== 'all') {
    if (rule.applicationArea !== applicationAreaFilter) return false;
  }
  if (statusFilter && statusFilter !== 'all') {
    if (rule.configurationStatus !== statusFilter) return false;
  }
  return true;
}

export function getListActionsForKeywordRule() {
  return ['view', 'edit'];
}

export function buildSampleKeywordConfigRules(sources = getKeywordConfigSources()) {
  const plumbing = sources.find((c) => /plumbing/i.test(c.name)) || sources[0];
  const electrical = sources.find((c) => /electrical/i.test(c.name)) || sources[1];
  const plumbingId = plumbing?.id || 'TC-001';
  const electricalId = electrical?.id || 'TC-002';
  const plumbingSubs = getTradeSubcategories(plumbing || {});
  const gasFitting =
    plumbingSubs.find((s) => /gas fitting/i.test(s.name)) || plumbingSubs[0];

  const samples = [
    {
      id: 'KWR-0001',
      ruleName: 'Post Creation — Plumbing',
      applicationArea: APPLICATION_AREA.POST_CREATION,
      ruleScope: RULE_SCOPE.SELECTED_TRADE_CATEGORIES,
      tradeCategoryIds: [plumbingId],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      keywordUse: KEYWORD_USE.ENABLED,
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: 'Saim Sahi',
      createdOn: '2025-02-10T09:20:00.000Z',
      lastUpdatedBy: 'Amelia Chen',
      lastUpdatedOn: '2026-07-18T14:40:00.000Z',
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: 'Saim Sahi',
          personId: '—',
          on: '2025-02-10T09:20:00.000Z',
        },
      ],
    },
    {
      id: 'KWR-0002',
      ruleName: 'Business Profile — All Keywords',
      applicationArea: APPLICATION_AREA.BUSINESS_PROFILE,
      ruleScope: RULE_SCOPE.ALL_KEYWORDS,
      tradeCategoryIds: [],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      keywordUse: KEYWORD_USE.ENABLED,
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: 'Saim Sahi',
      createdOn: '2025-02-12T11:10:00.000Z',
      lastUpdatedBy: 'Amelia Chen',
      lastUpdatedOn: '2026-07-20T10:15:00.000Z',
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: 'Saim Sahi',
          personId: '—',
          on: '2025-02-12T11:10:00.000Z',
        },
      ],
    },
    {
      id: 'KWR-0003',
      ruleName: 'Search Handoff — Gas Fitting Disabled',
      applicationArea: APPLICATION_AREA.SEARCH_MATCHING_HANDOFF,
      ruleScope: RULE_SCOPE.SELECTED_SUBCATEGORIES,
      tradeCategoryIds: [],
      parentTradeCategoryIds: [plumbingId],
      subcategoryIds: gasFitting?.id ? [gasFitting.id] : [],
      keywordUse: KEYWORD_USE.DISABLED,
      configurationStatus: CONFIGURATION_STATUS.ACTIVE,
      createdBy: 'Saim Sahi',
      createdOn: '2025-02-15T15:30:00.000Z',
      lastUpdatedBy: 'Amelia Chen',
      lastUpdatedOn: '2026-07-22T16:25:00.000Z',
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: 'Saim Sahi',
          personId: '—',
          on: '2025-02-15T15:30:00.000Z',
        },
      ],
    },
    {
      id: 'KWR-0004',
      ruleName: 'Post Creation — Electrical',
      applicationArea: APPLICATION_AREA.POST_CREATION,
      ruleScope: RULE_SCOPE.SELECTED_TRADE_CATEGORIES,
      tradeCategoryIds: [electricalId],
      parentTradeCategoryIds: [],
      subcategoryIds: [],
      keywordUse: KEYWORD_USE.ENABLED,
      configurationStatus: CONFIGURATION_STATUS.INACTIVE,
      createdBy: 'Saim Sahi',
      createdOn: '2025-02-18T10:40:00.000Z',
      lastUpdatedBy: 'Amelia Chen',
      lastUpdatedOn: '2026-07-23T09:15:00.000Z',
      changeHistory: [
        {
          action: 'Created',
          previousValues: null,
          newValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          by: 'Saim Sahi',
          personId: '—',
          on: '2025-02-18T10:40:00.000Z',
        },
        {
          action: 'Made Inactive',
          previousValues: { configurationStatus: CONFIGURATION_STATUS.ACTIVE },
          newValues: { configurationStatus: CONFIGURATION_STATUS.INACTIVE },
          by: 'Amelia Chen',
          personId: '—',
          on: '2026-07-23T09:15:00.000Z',
        },
      ],
    },
  ];

  return samples.map((row) => normalizeKeywordRule(row, sources));
}
